import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AppImage from '../../../components/AppImage';

const LuxuryCabs = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [itemsPerView, setItemsPerView] = useState(4);

  const luxuryCabs = [
  {
    name: 'Innova Hycross',
    price: '₹21',
    unit: 'km',
    seaters: 6,
    bags: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ece112f8-1768368473505.png",
    alt: 'White Toyota Innova Hycross luxury SUV with modern design'
  },
  {
    name: 'Kia Carens',
    price: '₹15',
    unit: 'km',
    seaters: 6,
    bags: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19f4ce200-1768361574511.png",
    alt: 'Silver Kia Carens MPV with spacious interior and modern features'
  },
  {
    name: 'Innova Crysta',
    price: '₹17',
    unit: 'km',
    seaters: 6,
    bags: 6,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b3582ff-1764720422661.png",
    alt: 'White Toyota Innova Crysta premium MPV with elegant exterior'
  },
  {
    name: 'Innova Crysta 7+1',
    price: '₹18',
    unit: 'km',
    seaters: 7,
    bags: 6,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b3582ff-1764720422661.png",
    alt: 'Pearl white Toyota Innova Crysta 7+1 seater with extra luggage capacity'
  },
  {
    name: 'Ertiga',
    price: '₹13',
    unit: 'km',
    seaters: 6,
    bags: 4,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6569b60-1766504910349.png",
    alt: 'Grey Maruti Suzuki Ertiga compact MPV with efficient design'
  },
  {
    name: 'Swift Dzire',
    price: '₹11',
    unit: 'km',
    seaters: 4,
    bags: 3,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18a7e19cb-1766947963493.png",
    alt: 'Red Maruti Suzuki Swift Dzire sedan with sleek aerodynamic body'
  }];


  const maxIndex = Math.max(0, luxuryCabs?.length - itemsPerView);

  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth < 640) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(4);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, maxIndex]);

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => prev <= 0 ? maxIndex : prev - 1);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
  };

  const handleVehicleClick = (cab) => {
    navigate('/vehicle-details', {
      state: {
        vehicle: {
          name: cab?.name === 'Ertiga' ? 'Maruti Ertiga' : cab?.name === 'Swift Dzire' ? 'Maruti Dzire' : cab?.name,
          bags: cab?.bags,
          seats: cab?.seaters,
          image: cab?.image,
          imageAlt: cab?.alt
        }
      }
    });
  };

  return (
    <section className="py-12 sm:py-14 md:py-16 bg-gradient-to-b from-teal-50 to-white">
      <div className="container mx-auto px-3 sm:px-4">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-orange-500 mb-3 sm:mb-4 px-4">
            Our Luxury Cabs
          </h2>
          <p className="text-gray-700 text-sm sm:text-base md:text-lg max-w-4xl mx-auto px-4">
            The Definitive Collection of Chauffeur-Driven Elegance: Unparalleled Luxury, Comfort, and Professionalism Await You
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button
            onClick={handlePrev}
            className="absolute left-0 sm:left-2 md:left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 sm:p-3 transition-all duration-300 hover:scale-110 touch-manipulation"
            aria-label="Previous vehicles">

            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 sm:right-2 md:right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 sm:p-3 transition-all duration-300 hover:scale-110 touch-manipulation"
            aria-label="Next vehicles">

            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
          </button>

          {/* Cards Container */}
          <div className="overflow-hidden mx-8 sm:mx-10 md:mx-12">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
                willChange: 'transform'
              }}>

              {luxuryCabs?.map((cab, index) =>
              <div
                key={index}
                className="flex-shrink-0 px-2 sm:px-3"
                style={{ width: `${100 / itemsPerView}%` }}>

                  <div
                  onClick={() => handleVehicleClick(cab)}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer transform hover:-translate-y-1 touch-manipulation">
                    {/* Image */}
                    <div className="relative h-36 sm:h-40 md:h-48 bg-gradient-to-br from-teal-100 to-teal-50">
                      <AppImage
                      src={cab?.image}
                      alt={cab?.alt}
                      className="w-full h-full object-contain p-3 sm:p-4" />

                    </div>

                    {/* Content */}
                    <div className="p-4 sm:p-5 md:p-6">
                      <div className="flex items-baseline justify-between mb-3 sm:mb-4">
                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800">
                          {cab?.name}
                        </h3>
                        <div className="text-right">
                          <span className="text-xl sm:text-2xl font-bold text-teal-600">
                            {cab?.price}
                          </span>
                          <span className="text-xs sm:text-sm text-teal-500 ml-1">
                            {cab?.unit}
                          </span>
                        </div>
                      </div>

                      {/* Specifications */}
                      <div className="flex justify-between text-xs sm:text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Seaters :</span>
                          <span className="ml-1">{cab?.seaters}</span>
                        </div>
                        <div>
                          <span className="font-medium">Bag :</span>
                          <span className="ml-1">{cab?.bags}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Indicators */}
        <div className="flex justify-center gap-2 mt-6 sm:mt-8">
          {Array.from({ length: maxIndex + 1 })?.map((_, index) =>
          <button
            key={index}
            onClick={() => {
              setIsAutoPlaying(false);
              setCurrentIndex(index);
            }}
            className={`h-2 rounded-full transition-all duration-300 touch-manipulation ${
            currentIndex === index ? 'w-8 bg-orange-500' : 'w-2 bg-gray-300'}`
            }
            aria-label={`Go to slide ${index + 1}`} />

          )}
        </div>
      </div>
    </section>);

};

export default LuxuryCabs;